import { useState } from 'react'
import { MetricCard } from './MetricCard'
import { Timeline } from './Timeline'
import { ScratchpadPanel } from './ScratchpadPanel'
import { ProjectSpendCard } from './ProjectSpendCard'
import { Shard, LogEntry } from '../types/shard'
import { Worker } from '../hooks/useWorkers'
import { useMetrics, MetricSnapshot } from '../hooks/useMetrics'
import { useActivityCost } from '../hooks/useActivityCost'
import { useInfraCost } from '../hooks/useInfraCost'
import { useProjectSpend, totalSpend } from '../hooks/useProjectSpend'

interface MissionControlProps {
  shards: Shard[]
  workers: Worker[]
  logs: LogEntry[]
  onShardClick: (shard: Shard) => void
  onWorkerClick: (worker: Worker) => void
  projectName: string
  residency: 'au' | 'none'
  getAuthHeaders: () => Record<string, string>
}

function formatDuration(ms: number): string {
  if (ms === 0) return '--'
  const minutes = Math.floor(ms / 60000)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) return `${hours}h ${minutes % 60}m`
  return `${minutes}m`
}

function extractSparkline(snapshots: MetricSnapshot[], field: keyof MetricSnapshot): number[] {
  return snapshots.map(s => s[field] as number)
}

export function MissionControl({ shards, workers, logs, onShardClick, onWorkerClick: _onWorkerClick, projectName, residency, getAuthHeaders }: MissionControlProps) {
  const { snapshots, segments, workerLifespans, currentMetrics } = useMetrics(shards, workers, logs)
  const activityMetrics = useActivityCost(logs)
  const infraCostMetrics = useInfraCost(logs)
  const spend = useProjectSpend(projectName, getAuthHeaders)
  const [selectedWorkerName, setSelectedWorkerName] = useState<string | null>(null)
  const [showCostDetails, setShowCostDetails] = useState(false)
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches

  // Top-line cost = authoritative LiteLLM spend (sponsorship + cash) + grounded ACA infra.
  // No invented Anthropic token estimate — see useActivityCost.ts for why that was removed.
  const llmSpend = totalSpend(spend.summary)
  const combinedCost = llmSpend + infraCostMetrics.totalInfraCost
  const hasSpendData = spend.summary !== null
  const hasCostData = hasSpendData || infraCostMetrics.totalRuns > 0

  // Real model breakdown straight from LiteLLM /spend (replaces invented opus/sonnet bar).
  const spendByModel = spend.summary?.by_model ?? []

  // Build cost tooltip breakdown from real data only.
  const costTooltip = hasCostData
    ? [
        `LiteLLM spend: $${llmSpend.toFixed(2)} (${spend.summary?.request_count ?? 0} req)`,
        `Infra: $${infraCostMetrics.totalInfraCost.toFixed(4)} (${infraCostMetrics.totalRuns} runs)`,
        '',
        ...spendByModel.map(m => `${m.model}: $${m.spend.toFixed(2)} (${m.request_count} req)`),
      ].join('\n')
    : undefined

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Metrics strip */}
      <div className="flex gap-3 px-4 py-3 overflow-x-auto border-b border-cosmic-border bg-cosmic-abyss/50 flex-shrink-0 max-lg:grid max-lg:grid-cols-4 max-lg:gap-0 max-lg:px-2 max-lg:py-1">
        <MetricCard
          title="Completed"
          value={currentMetrics.tasksCompleted}
          sparklineData={extractSparkline(snapshots, 'tasksCompleted')}
          sparklineColor="#14b8a6"
          compact={isMobile}
        />
        <MetricCard
          title="In Flight"
          value={currentMetrics.tasksInFlight}
          sparklineData={extractSparkline(snapshots, 'tasksInFlight')}
          sparklineColor="#06b6d4"
          compact={isMobile}
        />
        <MetricCard
          title="Blocked"
          value={currentMetrics.tasksBlocked}
          sparklineData={extractSparkline(snapshots, 'tasksBlocked')}
          sparklineColor="#ec4899"
          compact={isMobile}
        />
        <MetricCard
          title="Workers"
          value={`${currentMetrics.activeWorkers}/${currentMetrics.totalWorkers}`}
          sparklineData={extractSparkline(snapshots, 'activeWorkers')}
          sparklineColor="#8b5cf6"
          compact={isMobile}
        />
        <MetricCard
          title="Avg Cycle"
          value={formatDuration(currentMetrics.avgCycleTimeMs)}
          sparklineData={[]}
          sparklineColor="#fbbf24"
          compact={isMobile}
        />
        <MetricCard
          title="Specs"
          value={`${currentMetrics.specsCompleted}/${currentMetrics.totalSpecs}`}
          sparklineData={extractSparkline(snapshots, 'specsCompleted')}
          sparklineColor="#6366f1"
          compact={isMobile}
        />
        <MetricCard
          title="Cost"
          value={hasCostData ? `$${combinedCost.toFixed(2)}` : '--'}
          sparklineData={[]}
          sparklineColor="#fbbf24"
          subtitle={hasCostData ? `LiteLLM + Infra` : undefined}
          tooltip={costTooltip}
          compact={isMobile}
        />
      </div>

      {/* Cost breakdown bar — real LiteLLM spend by model (not invented opus/sonnet) */}
      {(spendByModel.length > 0 || activityMetrics.totalLoops > 0) && (
        <div className="flex items-center gap-4 px-4 py-1.5 border-b border-cosmic-border/50 bg-cosmic-abyss/30 flex-shrink-0 text-xs text-gray-500 max-lg:hidden">
          <span className="text-gray-600">Spend by model:</span>
          {spendByModel.length === 0 && (
            <span className="text-gray-600 italic">no LiteLLM spend (Claude OAuth / flat-rate)</span>
          )}
          {spendByModel.slice(0, 5).map(m => (
            <span key={m.model} className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${m.vendor === 'sponsorship' ? 'bg-cyan-500' : 'bg-yellow-500'}`} />
              <span className="text-gray-400">{m.model}</span>
              <span className="text-white">${m.spend.toFixed(2)}</span>
              <span className="text-gray-600">({m.request_count})</span>
            </span>
          ))}
          <span className="text-gray-600 ml-auto">Loops by persona:</span>
          {activityMetrics.byPersona.slice(0, 5).map(p => (
            <span key={p.persona} className="text-gray-400">
              {p.persona} <span className="text-white">{p.loops}</span>
            </span>
          ))}
          {activityMetrics.byPersona.length > 5 && (
            <span className="text-gray-600">+{activityMetrics.byPersona.length - 5} more</span>
          )}
          <button
            onClick={() => setShowCostDetails(!showCostDetails)}
            className="ml-2 text-cosmic-nebula-light hover:text-white transition-colors"
          >
            {showCostDetails ? 'Hide Details' : 'Cost Details'}
          </button>
        </div>
      )}

      {/* Expandable cost details */}
      {showCostDetails && (
        <div className="border-b border-cosmic-border/50 bg-cosmic-abyss/20 flex-shrink-0 max-h-64 overflow-y-auto">
          <ProjectSpendCard
            residency={residency}
            activity={activityMetrics}
            summary={spend.summary}
            loading={spend.loading}
            error={spend.error}
            window={spend.window}
            setWindow={spend.setWindow}
            reload={spend.reload}
          />
        </div>
      )}

      {/* Main content area */}
      <div className="flex flex-1 min-h-0 min-w-0 max-lg:flex-col">
        <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
          <Timeline
            segments={segments}
            workerLifespans={workerLifespans}
            shards={shards}
            onShardClick={onShardClick}
            onWorkerClick={(name) => setSelectedWorkerName(name)}
          />
        </div>
        <ScratchpadPanel
          workerName={selectedWorkerName}
          workers={workers}
          shards={shards}
          onClose={() => setSelectedWorkerName(null)}
          onShardClick={onShardClick}
        />
      </div>
    </div>
  )
}
